import { z } from 'zod';
import { amountScale, currencyCodeSchema } from './currency.js';
import { addMoney, convertAmount, dealRate, displayScale, exchangeRateSchema, fitsCurrencyScale, formatMoney, isNegativeMoney, isZeroMoney, moneyAmountSchema, negateMoney, subtractMoney, } from './money.js';
import { categoryLineSchema, descriptionSchema, isoDateSchema, payeeNameSchema, positiveAmountSchema, } from './transactions.js';
/**
 * Money in two currencies (Phase 4 PR 3), both booked through the family's FX trading accounts
 * (one equity account per currency) so every currency still sums to zero (CLAUDE.md §3.2):
 * - Exchange: one currency leaves an account, another arrives (in the same or another account).
 * - Expense paid in another currency: the bill in one currency, paid in a currency, change
 *   returned in a currency. Only the bill is an expense.
 */
const isMoneyString = (v) => typeof v === 'string' && moneyAmountSchema.safeParse(v).success;
/** Zero or more: change that may not have been given. */
export const nonNegativeAmountSchema = moneyAmountSchema.refine((v) => isMoneyString(v) && !isNegativeMoney(v), 'Amount cannot be negative');
const scaleIssue = (currency) => amountScale(currency) === 0
    ? `${currency} amounts must be whole numbers`
    : `${currency} amounts allow at most ${amountScale(currency)} decimals`;
/** POST /api/v1/transactions/exchange (Idempotency-Key required). */
export const exchangeRequestSchema = z
    .object({
    /** Where the money left: a money account (as people see it) and its currency. */
    fromAccountId: z.string().uuid(),
    fromCurrency: currencyCodeSchema,
    fromAmount: positiveAmountSchema,
    /** Where the other currency arrived; may be the same account in another currency. */
    toAccountId: z.string().uuid(),
    toCurrency: currencyCodeSchema,
    toAmount: positiveAmountSchema,
    occurredOn: isoDateSchema.optional(),
    description: descriptionSchema.optional(),
})
    .superRefine((v, ctx) => {
    if (v.fromCurrency === v.toCurrency) {
        ctx.addIssue({
            code: 'custom',
            path: ['toCurrency'],
            message: 'An exchange is between two different currencies',
        });
    }
    for (const [path, amount, currency] of [
        ['fromAmount', v.fromAmount, v.fromCurrency],
        ['toAmount', v.toAmount, v.toCurrency],
    ]) {
        if (isMoneyString(amount) && !fitsCurrencyScale(amount, amountScale(currency))) {
            ctx.addIssue({ code: 'custom', path: [path], message: scaleIssue(currency) });
        }
    }
});
const moneyPart = (amount) => z.object({ amount, currency: currencyCodeSchema });
/** One payment line: an amount in one currency, from (or into) one money account. */
const accountPart = (amount) => z.object({ accountId: z.string().uuid(), amount, currency: currencyCodeSchema });
/**
 * The person who is owed (expense) or owes (income) whatever the payments do not cover. The
 * amount is worked out: the bill minus what was paid, at the family rate of that day.
 */
const splitCreditSchema = z.object({
    personId: z.string().uuid().optional(),
    personName: payeeNameSchema.optional(),
    dueOn: isoDateSchema.optional(),
});
const foreignExpenseObjectSchema = z
    .object({
    /** An expense (paid out, change back) or an income (received, change given back). */
    kind: z.enum(['expense', 'income']).default('expense'),
    /** One category for the whole expense, or `lines` for a split (exactly one of the two). */
    categoryId: z.string().uuid().optional(),
    /**
     * A split: category lines in the expense's currency (the bill's, or the estimate's) that
     * add up to it.
     */
    lines: z.array(categoryLineSchema).min(1).max(20).optional(),
    /** What the shop asked for. Empty: estimated as paid minus returned at the family rate. */
    bill: moneyPart(positiveAmountSchema).optional(),
    /**
     * What was handed over (for an income: what was received), one line per account and
     * currency. Each account must already hold its currency.
     */
    payments: z.array(accountPart(positiveAmountSchema)).min(1).max(10),
    /**
     * The change (for an income: change given back), in any currency the family uses; zero
     * lines are ignored. A currency the account does not hold yet is added to it.
     */
    returns: z.array(accountPart(nonNegativeAmountSchema)).max(10).default([]),
    /** Part on credit: what the payments leave unpaid of the bill becomes a debt. */
    credit: splitCreditSchema.optional(),
    /**
     * Without a bill only: a one-off rate (1 base = rate) for the estimate, when the family has
     * no rate on that day.
     */
    rate: exchangeRateSchema.optional(),
    payeeId: z.string().uuid().optional(),
    payeeName: payeeNameSchema.optional(),
    occurredOn: isoDateSchema.optional(),
    description: descriptionSchema.optional(),
    sharedWithFamily: z.boolean().default(false),
})
    .superRefine((v, ctx) => {
    const parts = [
        [['bill'], v.bill],
        ...(Array.isArray(v.payments) ? v.payments : []).map((p, i) => [['payments', i], p]),
        ...(Array.isArray(v.returns) ? v.returns : []).map((p, i) => [['returns', i], p]),
    ];
    for (const [path, part] of parts) {
        if (part && isMoneyString(part.amount) && typeof part.currency === 'string') {
            if (!fitsCurrencyScale(part.amount, amountScale(part.currency))) {
                ctx.addIssue({
                    code: 'custom',
                    path: [...path, 'amount'],
                    message: scaleIssue(part.currency),
                });
            }
        }
    }
    if (v.payeeId !== undefined && v.payeeName !== undefined) {
        ctx.addIssue({
            code: 'custom',
            path: ['payeeName'],
            message: 'Give a payee id or a payee name, not both',
        });
    }
    if ((v.categoryId === undefined) === (v.lines === undefined)) {
        ctx.addIssue({
            code: 'custom',
            path: ['categoryId'],
            message: 'Give a category or split lines, not both',
        });
    }
    if (v.rate !== undefined && v.bill !== undefined) {
        ctx.addIssue({
            code: 'custom',
            path: ['rate'],
            message: 'With a bill, the rate comes from the amounts',
        });
    }
    if (v.credit) {
        if (v.bill === undefined) {
            ctx.addIssue({
                code: 'custom',
                path: ['bill'],
                message: 'Part on credit needs the bill',
            });
        }
        if ([v.credit.personId, v.credit.personName].filter((x) => x !== undefined).length !== 1) {
            ctx.addIssue({ code: 'custom', path: ['credit', 'personId'], message: 'Pick the person' });
        }
    }
});
/**
 * The first version had one account, one paid part and one returned part; it is still
 * accepted (an older app may send it during a deploy) and read as one line each.
 */
function fromSingleAccount(raw) {
    if (typeof raw !== 'object' || raw === null || 'payments' in raw || !('paid' in raw)) {
        return raw;
    }
    const { accountId, paid, returned, ...rest } = raw;
    const line = (part) => typeof part === 'object' && part !== null ? { accountId, ...part } : part;
    return {
        ...rest,
        payments: [line(paid)],
        returns: returned === undefined ? [] : [line(returned)],
    };
}
/**
 * POST /api/v1/transactions/foreign-expense (Idempotency-Key required): an expense or income
 * paid with several payments, in one or more currencies and accounts, with change returned.
 * Its correction: POST /api/v1/transactions/:id/correct-foreign-expense.
 */
export const foreignExpenseRequestSchema = z.preprocess(fromSingleAccount, foreignExpenseObjectSchema);
const asList = (part) => part === undefined ? [] : Array.isArray(part) ? [...part] : [part];
/**
 * Works out the postings of an expense paid in parts, in one or more currencies and accounts
 * (as an expense: paid out negative, the category positive; the API flips every sign for an
 * income). Shared by the API (which records it) and the Add screen (which shows the result as
 * the person types).
 *
 * `rates` are the family rates (1 base = rate) for the currencies involved; they are needed
 * only to estimate a missing bill, or to work out what is left on credit. The base currency's
 * rate is always 1.
 */
export function planForeignExpense(input, baseCurrency, rates) {
    const paid = asList(input.paid);
    const returned = asList(input.returned).filter((r) => !isZeroMoney(r.amount));
    const rateOf = (currency) => (currency === baseCurrency ? '1' : rates[currency]);
    const add = (map, key, amount) => map.set(key, addMoney(map.get(key) ?? '0', amount));
    // What left the accounts, net of change, per currency (positive = paid out).
    const netPaid = new Map();
    for (const p of paid)
        add(netPaid, p.currency, p.amount);
    for (const r of returned)
        add(netPaid, r.currency, negateMoney(r.amount));
    /** The net paid in `currency`, at the family rates; a missing rate names its currency. */
    const valueIn = (currency) => {
        const toRate = rateOf(currency);
        let total = '0';
        for (const [c, amount] of netPaid) {
            if (c === currency) {
                total = addMoney(total, amount);
                continue;
            }
            const fromRate = rateOf(c);
            if (!fromRate)
                return { missing: c };
            if (!toRate)
                return { missing: currency };
            total = addMoney(total, convertAmount(amount, fromRate, toRate, displayScale(currency)));
        }
        return { amount: total };
    };
    let expense;
    let estimated = false;
    let usedFamilyRates = false;
    let creditLine = null;
    if (input.bill) {
        expense = { amount: formatMoney(input.bill.amount), currency: input.bill.currency };
        if (input.onCredit) {
            const value = valueIn(expense.currency);
            if ('missing' in value)
                return { ok: false, code: 'rate.required', currency: value.missing };
            usedFamilyRates = [...netPaid.keys()].some((c) => c !== expense.currency);
            const owed = subtractMoney(expense.amount, value.amount);
            if (!isNegativeMoney(owed) && !isZeroMoney(owed)) {
                creditLine = { currency: expense.currency, amount: negateMoney(owed) };
            }
        }
    }
    else {
        const paidCurrencies = new Set(paid.map((p) => p.currency));
        const currencies = new Set(netPaid.keys());
        // One currency throughout: exact. Otherwise the estimate is in the paid currency when all
        // payments share one, else in the family currency.
        const currency = paidCurrencies.size === 1
            ? paid[0].currency
            : currencies.size === 1
                ? [...currencies][0]
                : baseCurrency;
        const value = valueIn(currency);
        if ('missing' in value)
            return { ok: false, code: 'rate.required', currency: value.missing };
        expense = { amount: value.amount, currency };
        estimated = [...netPaid].some(([c, amount]) => c !== currency && !isZeroMoney(amount));
        usedFamilyRates = estimated;
    }
    if (isNegativeMoney(expense.amount) || isZeroMoney(expense.amount)) {
        return { ok: false, code: 'foreign_expense.nothing_spent' };
    }
    // Each account: paid out (credit), change in (debit), netted per account and currency.
    const account = new Map();
    const addAccount = (p, amount) => {
        const key = `${p.accountId ?? ''}|${p.currency}`;
        const line = account.get(key);
        account.set(key, {
            ...(p.accountId === undefined ? {} : { accountId: p.accountId }),
            currency: p.currency,
            amount: addMoney(line?.amount ?? '0', amount),
        });
    };
    for (const p of paid)
        addAccount(p, negateMoney(p.amount));
    for (const r of returned)
        addAccount(r, r.amount);
    const accountLines = [...account.values()].filter((l) => !isZeroMoney(l.amount));
    // Whatever does not balance in a currency goes through that currency's FX trading account.
    const net = new Map();
    for (const [c, amount] of netPaid)
        add(net, c, negateMoney(amount));
    add(net, expense.currency, expense.amount);
    if (creditLine)
        add(net, creditLine.currency, creditLine.amount);
    const fxLines = [...net].flatMap(([currency, amount]) => isZeroMoney(amount) ? [] : [{ currency, amount: negateMoney(amount) }]);
    // Nothing to exchange, or some currencies given for others: the FX lines must have both
    // signs (currency in and currency out). Anything else means the amounts are off.
    if (fxLines.length === 1 ||
        (fxLines.length > 1 && new Set(fxLines.map((l) => isNegativeMoney(l.amount))).size === 1)) {
        return { ok: false, code: 'foreign_expense.amounts_mismatch' };
    }
    let effectiveRate = null;
    const planRates = {};
    if (input.bill && !usedFamilyRates && fxLines.length === 2) {
        const base = fxLines.find((l) => l.currency === baseCurrency);
        const other = fxLines.find((l) => l.currency !== baseCurrency);
        if (base && other) {
            effectiveRate = { currency: other.currency, rate: dealRate(other.amount, base.amount) };
            planRates[other.currency] = effectiveRate.rate;
        }
    }
    return {
        ok: true,
        expense,
        estimated,
        effectiveRate,
        accountLines,
        creditLine,
        fxLines,
        rates: planRates,
        usedFamilyRates,
    };
}
/**
 * The live summary under the payment lines: the net paid (paid minus returned) and the bill in
 * the family currency at the family rates, and what remains to pay or was paid in excess.
 * Null when a rate is missing.
 */
export function splitPaymentSummary(input, baseCurrency, rates) {
    const toBase = (p) => {
        if (p.currency === baseCurrency)
            return formatMoney(p.amount);
        const rate = rates[p.currency];
        return rate ? convertAmount(p.amount, rate, '1', displayScale(baseCurrency)) : null;
    };
    const values = [
        ...input.paid.map(toBase),
        ...(input.returned ?? []).map((r) => {
            const v = toBase(r);
            return v === null ? null : negateMoney(v);
        }),
    ];
    if (values.some((v) => v === null))
        return null;
    const paid = addMoney('0', ...values);
    const bill = input.bill ? toBase(input.bill) : null;
    if (input.bill && bill === null)
        return null;
    const diff = bill === null ? '0' : subtractMoney(bill, paid);
    return {
        paid,
        bill,
        remaining: isNegativeMoney(diff) ? '0.0000' : diff,
        excess: isNegativeMoney(diff) ? negateMoney(diff) : '0.0000',
    };
}
/**
 * The category lines of an expense paid in another currency: the single category gets the whole
 * expense; a split's lines must be in the expense's currency (its scale) and add up to it.
 * Null when the split does not fit.
 */
export function foreignExpenseCategoryLines(input, expense) {
    if (!input.lines)
        return [{ categoryId: input.categoryId, amount: expense.amount }];
    const scale = amountScale(expense.currency);
    if (input.lines.some((l) => !fitsCurrencyScale(l.amount, scale)))
        return null;
    const total = addMoney('0', ...input.lines.map((l) => l.amount));
    if (!isZeroMoney(subtractMoney(total, expense.amount)))
        return null;
    return input.lines.map((l) => ({ ...l, amount: formatMoney(l.amount) }));
}
/**
 * The rate an exchange records with: when one side is the base currency, the deal's own rate
 * for the other currency (1 base = X), so the exchange is exact in base currency too. Between
 * two non-base currencies, null: each uses the family rate.
 */
export function exchangeDealRate(from, to, baseCurrency) {
    if (from.currency === baseCurrency) {
        return { currency: to.currency, rate: dealRate(to.amount, from.amount) };
    }
    if (to.currency === baseCurrency) {
        return { currency: from.currency, rate: dealRate(from.amount, to.amount) };
    }
    return null;
}
//# sourceMappingURL=exchange.js.map